// The hotel screens' words: the seven tile states and the stay pills
// (HANDOFF §17, the legends of boards rooms--tape / rooms--today), the
// housekeeping states, the sources, and the Arabic count agreement on the
// drawn forms («٤ ليالٍ», «بالغان · طفل», «١٢ غرفة», «نزيل سابق ٣ إقامات»).
// Display only; every number is the payload's.

import { AR_WEEKDAYS, toArabicDigits } from '@/lib/format';
import type { CountForms } from '@/lib/terminology';
import { countLabel } from '@/lib/terminology';
import type { HkStatus, StayPill, StaySource, TileState } from '../api';

/** The seven tiles, in legend order (board rooms--today). */
export const TILE_ORDER: readonly TileState[] = ['occupied', 'due_out', 'arriving', 'vacant_clean', 'vacant_dirty', 'inspect', 'maintenance'];

export const TILE_WORD: Readonly<Record<TileState, string>> = {
  occupied: 'مشغولة',
  due_out: 'مغادرة اليوم',
  arriving: 'وصول اليوم',
  vacant_clean: 'شاغرة نظيفة',
  vacant_dirty: 'شاغرة متسخة',
  inspect: 'بانتظار التفتيش',
  maintenance: 'صيانة',
};

/** HANDOFF §17 «Status → pill», the words of the boards' legends («مبدئي», «لم يصل» from the reservation board). */
export const PILL_WORD: Readonly<Record<StayPill, string>> = {
  tentative: 'مبدئي',
  confirmed: 'مؤكَّد',
  in_house: 'نزيل في الفندق',
  due_out: 'مغادرة اليوم',
  arriving: 'وصول اليوم',
  no_show: 'لم يصل',
  block: 'وفد أو شركة',
  ota: 'من قناة خارجية',
  maintenance: 'صيانة',
};

/** The room's housekeeping state on the panel («نظيفة» drawn; «٤ متسخة», «قيد التنظيف», «بانتظار التفتيش» from the boards' heads and legends). */
export const HK_WORD: Readonly<Record<HkStatus | 'cleaning', string>> = {
  clean: 'نظيفة',
  dirty: 'متسخة',
  cleaning: 'قيد التنظيف',
  inspect: 'بانتظار التفتيش',
  maintenance: 'صيانة',
};

/** The housekeeping task word («تنظيف يومي» drawn; erp-core's only task is `daily`). */
export function taskWord(task: string): string {
  return task === 'daily' ? 'تنظيف يومي' : task;
}

/**
 * The reservation's source on the panel («هاتف · سجاد» drawn). «قناة خارجية»
 * and «شركة» are the legend's words; walk-in is the terminology's walk-in;
 * the app has no drawn word (undrawn — «التطبيق»).
 */
export function sourceWord(source: StaySource, walkIn: string): string {
  switch (source) {
    case 'phone':
      return 'هاتف';
    case 'walk_in':
      return walkIn;
    case 'ota':
      return 'قناة خارجية';
    case 'company':
      return 'شركة';
    case 'app':
      return 'التطبيق';
    default:
      return source;
  }
}

const NIGHTS: CountForms = { one: 'ليلة واحدة', two: 'ليلتان', few: 'ليالٍ', many: 'ليلة' };
const ADULTS: CountForms = { one: 'بالغ واحد', two: 'بالغان', few: 'بالغين', many: 'بالغاً' };
const CHILDREN: CountForms = { one: 'طفل', two: 'طفلان', few: 'أطفال', many: 'طفلاً' };
const STAYS: CountForms = { one: 'إقامة واحدة', two: 'إقامتان', few: 'إقامات', many: 'إقامة' };
const ROOMS: CountForms = { one: 'غرفة واحدة', two: 'غرفتان', few: 'غرف', many: 'غرفة' };

/** «٤ ليالٍ» · «ليلتان» · «ليلة واحدة» · «١٢ ليلة». */
export const nightsLabel = (n: number) => countLabel(n, NIGHTS);
/** «بالغان» · «بالغ واحد» · «٣ بالغين». */
export const adultsLabel = (n: number) => countLabel(n, ADULTS);
/** «طفل» · «طفلان» · «٣ أطفال». */
export const childrenLabel = (n: number) => countLabel(n, CHILDREN);
/** «٣ إقامات» · «إقامتان». */
export const staysLabel = (n: number) => countLabel(n, STAYS);
/** «١٢ غرفة» · «٥ غرف» · «غرفة واحدة». */
export const roomsLabel = (n: number) => countLabel(n, ROOMS);

/** «بالغان · طفل» — the children part only when there are any. */
export function guestsLabel(adults: number, children?: number): string {
  return children && children > 0 ? `${adultsLabel(adults)} · ${childrenLabel(children)}` : adultsLabel(adults);
}

/** «الأحد ١٨» — the weekday and the day of a plain date. */
export function weekdayDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return `${AR_WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${toArabicDigits(d)}`;
}

/** «الأحد ١١ – الخميس ١٥ · ٤ ليالٍ» — the stay from its arrival to its departure date, with the server's night count. */
export function stayRangeLabel(from: string, to: string, nights: number): string {
  return `${weekdayDay(from)} – ${weekdayDay(to)} · ${nightsLabel(nights)}`;
}

/**
 * The floor's name: the label erp-core serves («الطابق الأول» — an ordinal, «الأجنحة · الطابق الخامس» for a floor of
 * suites), exactly as served. Only a payload that carries a floor NUMBER and no label (the tape's rooms, the room panel
 * of an erp-core that does not send one yet) falls back to «الطابق ١»; '' for a room with no floor.
 */
export function floorName(floor: number | null | undefined, label?: string | null): string {
  if (label) return label;
  return typeof floor === 'number' ? `الطابق ${toArabicDigits(floor)}` : '';
}

/** «الطابق ١ · ١٢ غرفة» — a floor head: the server's label + the count of its rows. */
export function floorHead(label: string, rooms: number): string {
  return label ? `${label} · ${roomsLabel(rooms)}` : roomsLabel(rooms);
}
